import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { TYPE_STYLES, isDuty } from "./ClassCalendar";

interface DashStudent {
  id: string;
  name: string;
  rank?: string | null;
  sprouts?: string | null;
  environment?: string | null;
  rocks?: string | null;
}

interface CalEntry {
  id: string;
  date: string;
  title: string;
  entry_type: string;
}

interface Overview {
  class_name: string;
  rank_counts: Record<string, number>;
  students: DashStudent[];
  outstanding_questions: { student: string; question: string; asked_date?: string }[];
  next_lesson: { date: string; title: string; entry_type: string } | null;
  recent_sessions: { id: string; date: string; topic?: string; attendance: Record<string, string> }[];
  upcoming_entries: CalEntry[];
}

const RANK_STYLES: Record<string, { bg: string; text: string }> = {
  A: { bg: "#f0fdf4", text: "#15803d" },
  B: { bg: "#fffbeb", text: "#b45309" },
  C: { bg: "#fff1f2", text: "#be123c" },
};

const ATT_DOT: Record<string, { color: string; label: string }> = {
  IP: { color: "#22c55e", label: "Present" },
  X: { color: "#ef4444", label: "Absent" },
  CU: { color: "#f59e0b", label: "Late" },
};
const ATT_CYCLE = ["IP", "CU", "X", ""];

const NEXT_TYPE_LABEL: Record<string, string> = { lesson: "Lesson", bb: "BB", special: "Special" };

const card: React.CSSProperties = {
  background: "var(--card, #fff)",
  border: "1px solid var(--border-light)",
  borderRadius: 12,
  padding: 16,
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 10,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  fontSize: 13,
  padding: 8,
  borderRadius: 10,
  border: "1px solid var(--border-light)",
  fontFamily: "'DM Sans', -apple-system, sans-serif",
  lineHeight: 1.5,
};

function fmtDate(s: string): string {
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function ClassDashboard({ classId, fruitInfo = true }: { classId: string; fruitInfo?: boolean }) {
  const [data, setData] = useState<Overview | null>(null);
  const [editing, setEditing] = useState<DashStudent | null>(null);
  const [fruitForm, setFruitForm] = useState({ rank: "", sprouts: "", environment: "", rocks: "" });
  const [calEditor, setCalEditor] = useState<{ entry: CalEntry | null } | null>(null);
  const [calForm, setCalForm] = useState({ date: "", title: "", entry_type: "lesson" });
  const [calShift, setCalShift] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/dashboard/class/${classId}`);
    setData(r.data);
  }, [classId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading…</div>;

  const daysToNext = data.next_lesson
    ? Math.round((new Date(data.next_lesson.date).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000)
    : null;

  const cycleAttendance = async (sessionId: string, studentId: string, current: string | undefined) => {
    const next = ATT_CYCLE[(ATT_CYCLE.indexOf(current || "") + 1) % ATT_CYCLE.length];
    // optimistic local update
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        recent_sessions: d.recent_sessions.map((s) => {
          if (s.id !== sessionId) return s;
          const att = { ...s.attendance };
          if (next) att[studentId] = next;
          else delete att[studentId];
          return { ...s, attendance: att };
        }),
      };
    });
    await api.put("/sessions/cell", { session_id: sessionId, student_id: studentId, status: next });
  };

  const openFruit = (s: DashStudent) => {
    setEditing(s);
    setFruitForm({ rank: s.rank || "", sprouts: s.sprouts || "", environment: s.environment || "", rocks: s.rocks || "" });
  };

  const saveFruit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/students/${editing.id}`, {
        rank: fruitForm.rank || null,
        sprouts: fruitForm.sprouts,
        environment: fruitForm.environment,
        rocks: fruitForm.rocks,
      });
      await load();
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const openCal = (entry: CalEntry | null) => {
    setCalEditor({ entry });
    setCalShift(false);
    setCalForm(
      entry
        ? { date: entry.date, title: entry.title, entry_type: entry.entry_type }
        : { date: new Date().toISOString().slice(0, 10), title: "", entry_type: "lesson" }
    );
  };

  const saveCal = async () => {
    if (!calForm.date || !calForm.title.trim()) return;
    setSaving(true);
    try {
      if (calEditor?.entry) {
        await api.patch(`/calendar/${classId}/${calEditor.entry.id}`, { date: calForm.date, title: calForm.title.trim(), entry_type: calForm.entry_type });
      } else if (calShift) {
        await api.post(`/calendar/${classId}/insert-shift`, { date: calForm.date, title: calForm.title.trim(), entry_type: calForm.entry_type });
      } else {
        await api.post(`/calendar/${classId}`, { date: calForm.date, title: calForm.title.trim(), entry_type: calForm.entry_type });
      }
      await load();
      setCalEditor(null);
    } finally {
      setSaving(false);
    }
  };

  const deleteCal = async () => {
    if (!calEditor?.entry) return;
    setSaving(true);
    try {
      await api.delete(`/calendar/${classId}/${calEditor.entry.id}`);
      await load();
      setCalEditor(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Top row: rank counts + next lesson */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 16 }}>
        {fruitInfo && (["A", "B", "C"] as const).map((r) => (
          <div key={r} style={{ ...card, textAlign: "center", background: RANK_STYLES[r].bg, border: "none", padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: RANK_STYLES[r].text }}>RANK {r}</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700, color: RANK_STYLES[r].text }}>
              {data.rank_counts[r] || 0}
            </div>
          </div>
        ))}
        <div style={{ ...card, textAlign: "center", padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>TOTAL FRUITS</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700 }}>{data.students.length}</div>
          {(data.rank_counts.unranked || 0) > 0 && (
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{data.rank_counts.unranked} unranked</div>
          )}
        </div>
        <div style={{ ...card, gridColumn: "span 2", minWidth: 200, padding: 12 }}>
          <div style={{ ...sectionTitle, marginBottom: 4 }}>Next lesson</div>
          {data.next_lesson ? (
            <>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 600, lineHeight: 1.25 }}>{data.next_lesson.title}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {NEXT_TYPE_LABEL[data.next_lesson.entry_type] || data.next_lesson.entry_type} · {fmtDate(data.next_lesson.date)}
                {daysToNext === 0 ? " · today" : daysToNext === 1 ? " · tomorrow" : daysToNext != null ? ` · in ${daysToNext} days` : ""}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>Nothing scheduled</div>
          )}
        </div>
      </div>

      {/* Middle row: compact attendance + calendar snapshot */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, auto) 1fr", gap: 12, marginBottom: 16, alignItems: "start" }}>
        <div style={card}>
          <div style={sectionTitle}>Attendance · last {data.recent_sessions.length} lessons · click to edit</div>
          {data.recent_sessions.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No lessons yet</div>
          ) : (
            <table style={{ borderCollapse: "collapse", fontSize: 11.5, width: "auto" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "1px 10px 1px 0", fontWeight: 600, color: "var(--text-muted)", background: "transparent" }}>Student</th>
                  {data.recent_sessions.map((s) => (
                    <th key={s.id} title={s.topic || s.date} style={{ padding: "1px 5px", fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap", background: "transparent" }}>
                      {fmtDate(s.date)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.students.map((st) => (
                  <tr key={st.id}>
                    <td style={{ padding: "1px 10px 1px 0", fontWeight: 600, whiteSpace: "nowrap", border: "none" }}>{st.name}</td>
                    {data.recent_sessions.map((s) => {
                      const status = s.attendance[st.id];
                      const dot = status ? ATT_DOT[status] : null;
                      return (
                        <td key={s.id} style={{ textAlign: "center", padding: "1px 5px", border: "none" }}>
                          <span
                            onClick={() => cycleAttendance(s.id, st.id, status)}
                            title={`${dot ? dot.label : "No record"} — click to change`}
                            style={{
                              display: "inline-block",
                              width: 9,
                              height: 9,
                              borderRadius: "50%",
                              cursor: "pointer",
                              background: dot ? dot.color : "transparent",
                              border: dot ? "none" : "1.5px solid var(--border-light)",
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={sectionTitle}>Calendar · upcoming</div>
            <button className="btn btn-secondary btn-sm" style={{ marginTop: -6 }} onClick={() => openCal(null)}>+ Add</button>
          </div>
          {data.upcoming_entries.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>Nothing upcoming</div>
          ) : (
            data.upcoming_entries.map((e) => {
              const s = TYPE_STYLES[e.entry_type] || TYPE_STYLES.lesson;
              return (
                <div
                  key={e.id}
                  onClick={() => openCal(e)}
                  style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0", borderBottom: "1px solid var(--border-light)", cursor: "pointer" }}
                >
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-muted)", width: 46, flexShrink: 0, paddingTop: 2 }}>{fmtDate(e.date)}</span>
                  <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 5, background: s.bg, color: s.text, borderRadius: 6, padding: "2px 7px", fontWeight: 600, fontSize: 12, lineHeight: 1.35 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, marginTop: 4, flexShrink: 0 }} />
                    {e.title}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Fruits: sprout / environment / rocks — "fruit_info" feature */}
      {fruitInfo && (
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitle}>Fruit overview · sprout / environment / rocks · click to edit</div>
        {data.students.map((s) => {
          const rs = s.rank ? RANK_STYLES[s.rank] : null;
          return (
            <div
              key={s.id}
              onClick={() => openFruit(s)}
              style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--border-light)", cursor: "pointer" }}
            >
              <span
                style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, background: rs ? rs.bg : "var(--bg)", color: rs ? rs.text : "var(--text-muted)" }}
              >
                {s.rank || "–"}
              </span>
              <div style={{ minWidth: 80, fontSize: 13.5, fontWeight: 700, paddingTop: 2, flexShrink: 0 }}>{s.name}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 12, lineHeight: 1.45, flex: 1 }}>
                {(["sprouts", "environment", "rocks"] as const).map((f) => (
                  <div key={f}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {f === "sprouts" ? "Sprout" : f === "environment" ? "Environment" : "Rocks"}
                    </div>
                    <div style={{ color: s[f] ? "var(--text)" : "var(--text-muted)", fontStyle: s[f] ? "normal" : "italic" }}>
                      {s[f] || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Outstanding questions */}
      <div style={card}>
        <div style={sectionTitle}>Outstanding questions ({data.outstanding_questions.length})</div>
        {data.outstanding_questions.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>None outstanding</div>
        ) : (
          data.outstanding_questions.map((q, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border-light)" }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                {q.student}
                {q.asked_date && <span style={{ fontWeight: 400, color: "var(--text-muted)" }}> · {q.asked_date}</span>}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{q.question}</div>
            </div>
          ))
        )}
      </div>

      {/* Fruit edit modal */}
      {editing && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 110 }} onClick={() => setEditing(null)}>
          <div style={{ background: "var(--card, #fff)", borderRadius: 14, padding: 22, width: 440, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 16, margin: "0 0 12px" }}>{editing.name}</h3>
            <div className="form-group">
              <label>Rank</label>
              <select value={fruitForm.rank} onChange={(e) => setFruitForm({ ...fruitForm, rank: e.target.value })} style={inputStyle}>
                <option value="">Unranked</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
            </div>
            {(["sprouts", "environment", "rocks"] as const).map((f) => (
              <div className="form-group" key={f} style={{ marginTop: 10 }}>
                <label>{f === "sprouts" ? "Sprout" : f === "environment" ? "Environment" : "Rocks"}</label>
                <textarea value={fruitForm[f]} onChange={(e) => setFruitForm({ ...fruitForm, [f]: e.target.value })} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveFruit} disabled={saving}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar entry modal */}
      {calEditor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 110 }} onClick={() => setCalEditor(null)}>
          <div style={{ background: "var(--card, #fff)", borderRadius: 14, padding: 22, width: 380, maxWidth: "90vw", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 16, margin: "0 0 12px" }}>{calEditor.entry ? "Edit entry" : "New entry"}</h3>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={calForm.date} onChange={(e) => setCalForm({ ...calForm, date: e.target.value })} style={inputStyle} />
            </div>
            <div className="form-group" style={{ marginTop: 10 }}>
              <label>Title</label>
              <textarea value={calForm.title} onChange={(e) => setCalForm({ ...calForm, title: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div className="form-group" style={{ marginTop: 10 }}>
              <label>Type</label>
              <select value={calForm.entry_type} onChange={(e) => setCalForm({ ...calForm, entry_type: e.target.value })} style={inputStyle}>
                {Object.entries(TYPE_STYLES).map(([k, s]) => (
                  <option key={k} value={k}>{s.label}</option>
                ))}
              </select>
            </div>
            {!calEditor.entry && !isDuty(calForm.entry_type) && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, marginTop: 10, cursor: "pointer", lineHeight: 1.4 }}>
                <input type="checkbox" checked={calShift} onChange={(e) => setCalShift(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong>Insert &amp; shift</strong> — push all later lessons back one class day
                  <span style={{ color: "var(--text-muted)" }}> (services, Passover and no-class days stay put)</span>
                </span>
              </label>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
              {calEditor.entry && (
                <button className="btn btn-danger btn-sm" onClick={deleteCal} disabled={saving} style={{ marginRight: "auto" }}>Delete</button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setCalEditor(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveCal} disabled={saving || !calForm.title.trim() || !calForm.date}>
                {calEditor.entry ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
